"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from "react";
import type { DensityMode, Direction, ThemeMode } from "@/lib/types";

const THEME_STORAGE_KEY = "hh-theme";
const DENSITY_STORAGE_KEY = "hh-density";
const DIRECTION_STORAGE_KEY = "hh-direction";

interface AppShellContextValue {
  theme: ThemeMode | "system";
  setTheme: (theme: ThemeMode | "system") => void;
  resolvedTheme: ThemeMode;
  density: DensityMode;
  setDensity: (density: DensityMode) => void;
  direction: Direction;
  setDirection: (direction: Direction) => void;
}

const AppShellContext = createContext<AppShellContextValue | null>(null);

/**
 * localStorage is an external system, so we read it through
 * useSyncExternalStore rather than "read once in an effect + setState"
 * (the latter causes an avoidable extra render and a real hydration
 * mismatch window; the former is the pattern React itself recommends for
 * subscribing to an external store, works for SSR and needs no seams).
 */
function subscribeToLocalKey(key: string) {
  return (onStoreChange: () => void) => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === key) onStoreChange();
    };
    const onLocalWrite = () => onStoreChange();
    window.addEventListener("storage", onStorage);
    window.addEventListener(localWriteEventName(key), onLocalWrite);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(localWriteEventName(key), onLocalWrite);
    };
  };
}

function localWriteEventName(key: string) {
  return `hh-local-write:${key}`;
}

function readLocal(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
    window.dispatchEvent(new Event(localWriteEventName(key)));
  } catch {
    // Private browsing / blocked storage — the in-memory context value
    // (set via the same call) still drives the current session correctly.
  }
}

function getServerSnapshotNull() {
  return null;
}

function subscribeToMediaQuery(query: string) {
  return (onStoreChange: () => void) => {
    const media = window.matchMedia(query);
    media.addEventListener("change", onStoreChange);
    return () => media.removeEventListener("change", onStoreChange);
  };
}

/**
 * Root client provider for the three cross-cutting shell modes required by
 * Phase 0: theme (light/dark), density (Operations/Standard/Immersive) and
 * direction (LTR/RTL). Each mode is reflected as a DOM attribute on
 * <html> so plain CSS (tokens.css) can react to it without prop drilling.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  const storedTheme = useSyncExternalStore(
    subscribeToLocalKey(THEME_STORAGE_KEY),
    () => readLocal(THEME_STORAGE_KEY),
    getServerSnapshotNull,
  ) as ThemeMode | "system" | null;
  const storedDensity = useSyncExternalStore(
    subscribeToLocalKey(DENSITY_STORAGE_KEY),
    () => readLocal(DENSITY_STORAGE_KEY),
    getServerSnapshotNull,
  ) as DensityMode | null;
  const storedDirection = useSyncExternalStore(
    subscribeToLocalKey(DIRECTION_STORAGE_KEY),
    () => readLocal(DIRECTION_STORAGE_KEY),
    getServerSnapshotNull,
  ) as Direction | null;

  const systemPrefersDark = useSyncExternalStore(
    subscribeToMediaQuery("(prefers-color-scheme: dark)"),
    () => window.matchMedia("(prefers-color-scheme: dark)").matches,
    () => false,
  );

  const theme = storedTheme ?? "system";
  const density = storedDensity ?? "standard";
  const direction = storedDirection ?? "ltr";
  const resolvedTheme: ThemeMode = theme === "system" ? (systemPrefersDark ? "dark" : "light") : theme;

  // Reflect state onto <html> as data attributes consumed by tokens.css.
  // These effects synchronize an external system (the DOM) FROM React state
  // — the pattern the set-state-in-effect rule explicitly recommends, as
  // opposed to calling setState inside an effect.
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") {
      root.removeAttribute("data-theme");
    } else {
      root.setAttribute("data-theme", theme);
    }
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute("data-density", density);
  }, [density]);

  useEffect(() => {
    document.documentElement.setAttribute("dir", direction);
    document.documentElement.lang = direction === "rtl" ? "ar" : "en";
  }, [direction]);

  const setTheme = useCallback((next: ThemeMode | "system") => {
    writeLocal(THEME_STORAGE_KEY, next);
  }, []);

  const setDensity = useCallback((next: DensityMode) => {
    writeLocal(DENSITY_STORAGE_KEY, next);
  }, []);

  const setDirection = useCallback((next: Direction) => {
    writeLocal(DIRECTION_STORAGE_KEY, next);
  }, []);

  const value = useMemo<AppShellContextValue>(
    () => ({ theme, setTheme, resolvedTheme, density, setDensity, direction, setDirection }),
    [theme, setTheme, resolvedTheme, density, setDensity, direction, setDirection],
  );

  return <AppShellContext.Provider value={value}>{children}</AppShellContext.Provider>;
}

export function useAppShell() {
  const ctx = useContext(AppShellContext);
  if (!ctx) {
    throw new Error("useAppShell must be used within <AppProviders>");
  }
  return ctx;
}
